/**
 * PWA utilities for handling full screen mode and orientation locking
 */

// Extend the global interface to include gesture events (iOS specific)
declare global {
  interface DocumentEventMap {
    'gesturestart': Event;
    'gesturechange': Event;
    'gestureend': Event;
  }
}

/**
 * Lock the screen orientation to portrait mode
 */
export const lockOrientationToPortrait = async (): Promise<void> => {
  try {
    // Check if screen orientation API is available
    if ('screen' in window && 'orientation' in window.screen) {
      const screenOrientation = window.screen.orientation as any;
      
      if ('lock' in screenOrientation) {
        await screenOrientation.lock('portrait');
        console.log('Screen orientation locked to portrait');
      }
    }
    
    // Fallback for older browsers
    if ('orientation' in window.screen && 'lockOrientation' in window.screen) {
      const screen = window.screen as any;
      screen.lockOrientation('portrait');
    }
  } catch (error) {
    console.warn('Could not lock screen orientation:', error);
  }
};

/**
 * Request fullscreen mode
 */
export const requestFullscreen = async (): Promise<void> => {
  try {
    const element = document.documentElement;
    
    if (element.requestFullscreen) {
      await element.requestFullscreen();
    } else if ((element as any).webkitRequestFullscreen) {
      await (element as any).webkitRequestFullscreen();
    } else if ((element as any).msRequestFullscreen) {
      await (element as any).msRequestFullscreen();
    }
    
    console.log('Fullscreen mode activated');
  } catch (error) {
    console.warn('Could not enter fullscreen mode:', error);
  }
};

/**
 * Exit fullscreen mode
 */
export const exitFullscreen = async (): Promise<void> => {
  try {
    if (document.exitFullscreen) {
      await document.exitFullscreen();
    } else if ((document as any).webkitExitFullscreen) {
      await (document as any).webkitExitFullscreen();
    } else if ((document as any).msExitFullscreen) {
      await (document as any).msExitFullscreen();
    }
    
    console.log('Fullscreen mode deactivated');
  } catch (error) {
    console.warn('Could not exit fullscreen mode:', error);
  }
};

/**
 * Check if the app is currently in fullscreen mode
 */
export const isFullscreen = (): boolean => {
  return !!(
    document.fullscreenElement ||
    (document as any).webkitFullscreenElement ||
    (document as any).msFullscreenElement
  );
};

/**
 * Check if the app is running as a PWA (installed)
 */
export const isPWA = (): boolean => {
  return window.matchMedia('(display-mode: fullscreen)').matches ||
         window.matchMedia('(display-mode: standalone)').matches ||
         (window.navigator as any).standalone === true;
};

/**
 * Initialize PWA features when the app loads
 */
export const initializePWA = async (): Promise<void> => {
  // Only apply PWA features if running as PWA
  if (isPWA()) {
    console.log('PWA mode detected, initializing features...');
    
    // Lock orientation to portrait
    await lockOrientationToPortrait();
    
    // Add event listeners for orientation changes
    window.addEventListener('orientationchange', () => {
      setTimeout(() => {
        lockOrientationToPortrait();
      }, 100);
    });
    
    // Prevent zoom on double tap (iOS Safari)
    let lastTouchEnd = 0;
    document.addEventListener('touchend', (event) => {
      const now = new Date().getTime();
      if (now - lastTouchEnd <= 300) {
        event.preventDefault();
      }
      lastTouchEnd = now;
    }, false);
    
    // Prevent pull-to-refresh and multi-touch gestures
    document.addEventListener('touchstart', (event) => {
      if (event.touches.length > 1) {
        event.preventDefault();
      }
    }, { passive: false });
    
    document.addEventListener('touchmove', (event) => {
      // Prevent multi-touch gestures (pinch to zoom)
      if (event.touches.length > 1) {
        event.preventDefault();
      }
    }, { passive: false });
    
    // Prevent gesture events (iOS specific)
    document.addEventListener('gesturestart', (event) => {
      event.preventDefault();
    }, { passive: false });
    
    document.addEventListener('gesturechange', (event) => {
      event.preventDefault();
    }, { passive: false });
    
    document.addEventListener('gestureend', (event) => {
      event.preventDefault();
    }, { passive: false });
  }
};

/**
 * Add CSS to hide address bar and improve fullscreen experience
 */
export const addPWAStyles = (): void => {
  const style = document.createElement('style');
  style.textContent = `
    /* PWA Fullscreen Styles */
    @media (display-mode: fullscreen), (display-mode: standalone) {
      body {
        /* Ensure content fills the entire screen */
        margin: 0;
        padding: 0;
        overflow-x: hidden;
        
        /* Prevent user scaling */
        touch-action: manipulation;
        -webkit-touch-callout: none;
        -webkit-user-select: none;
        -khtml-user-select: none;
        -moz-user-select: none;
        -ms-user-select: none;
        user-select: none;
      }
      
      /* Hide scrollbars in fullscreen */
      ::-webkit-scrollbar {
        display: none;
      }
      
      /* Ensure proper viewport handling */
      html {
        height: 100vh;
        height: -webkit-fill-available;
      }
      
      body {
        min-height: 100vh;
        min-height: -webkit-fill-available;
      }
      
      #root {
        min-height: 100vh;
        min-height: -webkit-fill-available;
      }
    }
    
    /* iOS specific fixes */
    @supports (-webkit-touch-callout: none) {
      @media (display-mode: fullscreen), (display-mode: standalone) {
        /* Fix for iOS safe areas */
        body {
          padding-top: env(safe-area-inset-top);
          padding-bottom: env(safe-area-inset-bottom);
          padding-left: env(safe-area-inset-left);
          padding-right: env(safe-area-inset-right);
        }
      }
    }
  `;
  
  document.head.appendChild(style);
};